import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";
import logo from "../../public/ssplasticlogo.webp";
import QuoteModal from "./QuoteModal";
import PRODUCTS from "../data/products";

const navItems = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Products", href: "/products" },
  { name: "Gallery", href: "/gallery" },
  { name: "Contact Us", href: "/contact" },
];

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const location = useLocation();

  // Scroll detection for shadow
  useEffect(() => {
    const handler = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-egyptian-blue text-white text-xs sm:text-sm py-2 px-4 hidden md:block relative z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex gap-6">
              <a
                href="tel:+919825850501"
                className="flex items-center gap-2 hover:text-amber transition-colors"
              >
                <Phone className="h-3 w-3" />
                +91 98258 50501
              </a>
              <a
                href="mailto:info@ss-plastic.com"
                className="flex items-center gap-2 hover:text-amber transition-colors"
              >
                <Mail className="h-3 w-3" />
                info@ss-plastic.com
              </a>
              <span className="flex items-center gap-2">
                <MapPin className="h-3 w-3" />
                Vapi, Gujarat, India
              </span>
            </div>
            <span className="text-white/70">GST: 24BIRPS4800G2Z3</span>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav className={cn(
        "sticky inset-x-0 z-50 w-full transition-all duration-300",
        "top-0 md:top-0",
        "backdrop-blur-md bg-white/90 border-b border-gray-200",
        isScrolled && "shadow-lg"
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={logo}
                  alt="SS Plastic Logo"
                  className="h-11 w-auto"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center gap-1">
                {navItems.map((item) => (
                  <div key={item.name} className="relative">
                    {item.name === "Products" ? (
                      <div
                        className={cn(
                          "px-3.5 py-2 text-sm font-medium transition-all duration-250 relative group cursor-pointer flex items-center gap-1",
                          location.pathname === item.href || isProductsDropdownOpen
                            ? "text-egyptian-blue"
                            : "text-gray-700 hover:text-egyptian-blue"
                        )}
                        onMouseEnter={() => setIsProductsDropdownOpen(true)}
                        onMouseLeave={() => setIsProductsDropdownOpen(false)}
                      >
                        {item.name}
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          isProductsDropdownOpen ? "rotate-180" : ""
                        )} />
                        <span className={cn(
                          "absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-amber transition-all duration-250",
                          location.pathname === item.href || isProductsDropdownOpen ? "w-4/5" : "w-0 group-hover:w-3/5"
                        )} />
                        
                        {/* Products Dropdown Menu */}
                        <div 
                          className={cn(
                            "absolute top-full left-1/2 -translate-x-1/2 mt-3 w-screen max-w-7xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-400",
                            isProductsDropdownOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4"
                          )}
                          onMouseEnter={() => setIsProductsDropdownOpen(true)}
                          onMouseLeave={() => setIsProductsDropdownOpen(false)}
                        >
                          {/* Header Section */}
                          <div className="bg-gradient-to-r from-egyptian-blue to-violet-blue px-8 py-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-2xl font-bold text-white">
                                  Our Products
                                </h3>
                                <p className="text-white/90 mt-1">Premium FRP solutions for industrial applications</p>
                              </div>
                              <Link
                                to="/products"
                                className="group flex items-center gap-2 px-6 py-3 bg-white text-egyptian-blue font-semibold rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105"
                                onClick={() => setIsProductsDropdownOpen(false)}
                              >
                                View All Products
                                <ChevronDown className="h-4 w-4 rotate-270 group-hover:translate-x-1 transition-transform" />
                              </Link>
                            </div>
                          </div>

                          {/* Products Grid */}
                          <div className="p-8">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
                              {PRODUCTS.map((product, index) => (
                                <Link
                                  key={product.id}
                                  to={`/product/${product.id}`}
                                  className="group flex flex-col items-center gap-3 p-4 rounded-xl bg-white border border-gray-100 hover:border-egyptian-blue hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                                  onClick={() => setIsProductsDropdownOpen(false)}
                                >
                                  {/* Product Image Container */}
                                  <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-gray-50">
                                    <img
                                      src={product.image}
                                      alt={product.name}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                    {/* Badge */}
                                    {/* <div className="absolute -top-1 -right-1 px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md shadow-md">
                                      {product.badge}
                                    </div> */}
                                  </div>

                                  {/* Product Info */}
                                  <div className="text-center w-full">
                                    <h4 className="text-sm font-semibold text-gray-900 text-center leading-tight mb-1 group-hover:text-egyptian-blue transition-colors duration-300">
                                      {product.name}
                                    </h4>
                                    <p className="text-xs text-gray-500 text-center font-medium">
                                      {product.certification}
                                    </p>
                                  </div>
                                </Link>
                              ))}
                            </div>

                            {/* Bottom CTA Section */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                              <div className="flex items-center justify-center gap-8">
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-egyptian-blue">{PRODUCTS.length}</div>
                                  <div className="text-sm text-gray-600">Products</div>
                                </div>
                                <div className="w-px h-8 bg-gray-300" />
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-violet-blue">20+</div>
                                  <div className="text-sm text-gray-600">Years</div>
                                </div>
                                <div className="w-px h-8 bg-gray-300" />
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-amber-500">ISO</div>
                                  <div className="text-sm text-gray-600">Certified</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={cn(
                          "px-3.5 py-2 text-sm font-medium transition-all duration-250 relative group",
                          location.pathname === item.href
                            ? "text-egyptian-blue"
                            : "text-gray-700 hover:text-egyptian-blue"
                        )}
                      >
                        {item.name}
                        <span className={cn(
                          "absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-amber transition-all duration-250",
                          location.pathname === item.href ? "w-4/5" : "w-0 group-hover:w-3/5"
                        )} />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden md:block ml-6">
              <Button
                variant="cta"
                size="sm"
                onClick={() => setIsModalOpen(true)}
              >
                Get Quote
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-4 space-y-1 bg-white border-t border-gray-100">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "block px-4 py-3 text-base font-medium transition-colors duration-200 rounded-lg",
                    location.pathname === item.href
                      ? "text-egyptian-blue bg-amber/10 font-medium"
                      : "text-gray-700 hover:text-egyptian-blue hover:bg-gray-100"
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              <div className="px-3 pt-4 mt-2 border-t border-gray-100">
                <Button
                  variant="cta"
                  size="lg"
                  className="w-full"
                  onClick={() => {
                    setIsOpen(false);
                    setIsModalOpen(true);
                  }}
                >
                  Get Quote
                </Button>
              </div>

              {/* Mobile contact info */}
              <div className="mt-4 px-4 space-y-2 text-sm text-gray-600">
                <a href="tel:+919825850501" className="flex items-center gap-2 py-1.5">
                  <Phone className="h-4 w-4 text-amber" />
                  +91 98258 50501
                </a>
                <a href="mailto:info@ss-plastic.com" className="flex items-center gap-2 py-1.5">
                  <Mail className="h-4 w-4 text-amber" />
                  info@ss-plastic.com
                </a>
              </div>

            </div>
          </div>
        )}

        <QuoteModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      </nav>
    </>
  );
};

export default Navigation;